import React, { forwardRef, useRef, useImperativeHandle, useEffect } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { es } from "date-fns/locale";
import styles from "./date-time-picker.module.css";
import { MyIcons } from "../../icons/my-icons";
import "react-datepicker/dist/react-datepicker.css";

// Registrar el locale español
registerLocale("es", es);

const DateTimePicker = forwardRef(({
  label,
  selected,
  onChange,
  placeholder,
  dateFormat = "dd/MM/yyyy HH:mm",
  timeFormat = "HH:mm",
  showTimeSelect = true,
  disabled = false,
  className,
  wrapperClassName,
  style,
  wrapperStyle,
  name,
  dateOnly = false, // Nueva prop para mostrar solo fecha
}, ref) => {
  const pickerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    setOpen: (isOpen) => {
      if (pickerRef.current && pickerRef.current.setOpen) {
        pickerRef.current.setOpen(isOpen);
      }
    },
    // expose focus for convenience
    focus: () => {
      if (pickerRef.current && pickerRef.current.input) {
        pickerRef.current.input.focus();
      }
    }
  }));

  // Force input width on the internal input element in case react-datepicker
  // sets an inline width (common when wrapping in other layouts). We update
  // the inline style so it fills the container.
  useEffect(() => {
    try {
      const inputEl = pickerRef.current && pickerRef.current.input;
      if (inputEl) {
        inputEl.style.width = '100%';
        inputEl.style.minWidth = '0';
        inputEl.style.maxWidth = '100%';
        inputEl.style.boxSizing = 'border-box';
      }
    } catch (e) {
      // ignore errors
    }
  }, [pickerRef.current, selected]);

  // Filter function to only allow times between 6:00 and 20:00 with minutes at 0, 15, 30, 45
  const filterTime = (time) => {
    if (!time) return false;
    
    const hours = time.getHours();
    const minutes = time.getMinutes();
    
    // Only allow hours between 6 and 20
    if (hours < 6 || hours > 20) {
      return false;
    }
    
    // For hour 20, only allow minute 0
    if (hours === 20) {
      return minutes === 0;
    }
    
    // For hours 6-19, only allow minutes 0, 15, 30, 45
    return [0, 15, 30, 45].includes(minutes);
  };

  return (
    <div className={`${styles.wrapper} ${wrapperClassName || ""}`} style={style}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.inner} style={wrapperStyle}>
        <DatePicker
          ref={pickerRef}
          selected={selected}
          onChange={onChange}
          showTimeSelect={!dateOnly}
          timeFormat="HH:mm"
          timeIntervals={15}
          dateFormat={dateOnly ? "dd/MM/yyyy" : dateFormat}
          placeholderText={placeholder}
          className={`${styles.input} ${className || ""}`}
          disabled={disabled}
          name={name}
          filterTime={dateOnly ? undefined : filterTime}
          timeCaption="Hora"
          calendarStartDay={1}
          locale="es"
        />
        <div
          className={styles.icon}
          onClick={() => pickerRef.current && pickerRef.current.setOpen && pickerRef.current.setOpen(true)}
        >
          <MyIcons name="calendar" width={18} height={18} color={"var(--dark-grey)"} />
        </div>
      </div>
    </div>
  );
});

export default DateTimePicker;
